import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import NavbarAuth from '../components/NavbarAuth';
import api from '../services/api';
import PAYS from '../utils/pays';
import ErrorBoundary from '../components/ErrorBoundary';

// ═══ Avatars prédéfinis ═══
// Déposez vos images dans public/avatars/ avec ces noms exacts.
// Les chemins sont servis directement (pas d'import), et stockés tels
// quels dans photo_url — ils s'affichent donc partout automatiquement.
const AVATARS = [
    { id: 'homme-1', genre: 'homme', src: '/avatars/homme-1.png' },
    { id: 'homme-2', genre: 'homme', src: '/avatars/homme-2.png' },
    { id: 'homme-3', genre: 'homme', src: '/avatars/homme-3.png' },
    { id: 'homme-4', genre: 'homme', src: '/avatars/homme-4.png' },
    { id: 'femme-1', genre: 'femme', src: '/avatars/femme-1.png' },
    { id: 'femme-2', genre: 'femme', src: '/avatars/femme-2.png' },
    { id: 'femme-3', genre: 'femme', src: '/avatars/femme-3.png' },
    { id: 'femme-4', genre: 'femme', src: '/avatars/femme-4.png' },
];

const useResponsiveLocal = () => {
    const getState = () => {
        const w = window.innerWidth;
        return { isSmall: w <= 480, isMobile: w <= 768, isTablet: w > 768 && w <= 968 };
    };
    const [state, setState] = useState(getState);
    useEffect(() => {
        const handleResize = () => setState(getState());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return state;
};

const LANGUES = ['Français', 'Arabe', 'Malgache', 'Anglais', 'Wolof', 'Bambara', 'Comorien', 'Berbère', 'Turc', 'Espagnol', 'Autre'];

const ETHNIES = [
    'Arabe', 'Berbère', 'Africaine subsaharienne', 'Malgache', 'Comorienne',
    'Turque', 'Kurde', 'Persane', 'Sud-asiatique (Inde/Pakistan)',
    'Asiatique de l\'Est', 'Européenne', 'Métisse', 'Autre'
];

const GOLD = '#c0306a';
const Icon = ({ path, size = 18, color = GOLD }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>{path}</svg>
);

// Select avec option « Autre » : quand « Autre » est choisi (ou que la valeur
// enregistrée ne fait pas partie des options connues), un champ texte apparaît
// pour saisir une valeur libre. La valeur saisie est stockée directement
// dans le champ (approche simple, pas de colonne séparée en base).
const SelectAvecAutre = ({ name, value, onChange, options, style, autreLabels = ['Autre', 'autre'] }) => {
    // Les "valeurs connues" sont celles proposées dans les options (hors vide et hors 'Autre')
    const valeursConnues = options
        .map(o => (typeof o === 'string' ? o : o.value))
        .filter(v => v !== '' && !autreLabels.includes(v));

    // Est-ce que la valeur actuelle est une valeur libre (donc "Autre") ?
    const estValeurLibre = value && !valeursConnues.includes(value);

    // L'état "mode Autre" est vrai si l'utilisateur a choisi Autre, ou si la
    // valeur enregistrée est une valeur libre (au rechargement du profil).
    const [modeAutre, setModeAutre] = React.useState(estValeurLibre);

    // Si la valeur change de l'extérieur vers une valeur libre, on active le mode Autre.
    React.useEffect(() => {
        if (estValeurLibre) setModeAutre(true);
    }, [estValeurLibre]);

    const handleSelect = (e) => {
        const v = e.target.value;
        if (autreLabels.includes(v)) {
            // Passage en mode Autre : on vide la valeur pour laisser saisir
            setModeAutre(true);
            onChange({ target: { name, value: '' } });
        } else {
            setModeAutre(false);
            onChange({ target: { name, value: v } });
        }
    };

    // La valeur affichée dans le select : si mode Autre, on montre 'Autre'
    const valeurSelect = modeAutre ? (autreLabels[0]) : (value || '');

    // Un seul champ à la fois : soit le menu déroulant, soit le champ de saisie libre.
    if (modeAutre) {
        return (
            <input
                type="text"
                name={name}
                value={value || ''}
                onChange={(e) => {
                    const v = e.target.value;
                    onChange({ target: { name, value: v } });
                    // Filet de sécurité : si l'utilisateur vide entièrement le champ,
                    // on revient au menu déroulant (sinon il resterait bloqué).
                    if (v === '') setModeAutre(false);
                }}
                placeholder="Veuillez préciser…"
                style={style}
                autoFocus
            />
        );
    }

    return (
        <select name={name} value={valeurSelect} onChange={handleSelect} style={style}>
            {options.map((o, i) => {
                const val = typeof o === 'string' ? o : o.value;
                const lab = typeof o === 'string' ? o : o.label;
                return <option key={i} value={val}>{lab}</option>;
            })}
        </select>
    );
};

const icons = {
    user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
    pin: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></>,
    building: <><rect x="4" y="2" width="16" height="20" rx="2" /><line x1="9" y1="9" x2="9" y2="9.01" /><line x1="15" y1="9" x2="15" y2="9.01" /></>,
    briefcase: <><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></>,
    cap: <><path d="M22 10L12 5 2 10l10 5 10-5z" /><path d="M6 12v5c0 1 2.5 3 6 3s6-2 6-3v-5" /></>,
    heart: <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />,
    users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    edit: <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></>,
    camera: <><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></>,
    save: <><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></>,
    home: <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></>,
    mail: <><rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22,6 12,13 2,6" /></>,
    crown: <path d="M2 18h20l-2-9-5 4-3-6-3 6-5-4-2 9z" />,
    headset: <><path d="M3 18v-6a9 9 0 0 1 18 0v6" /><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" /></>,
    upload: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></>,
    ruler: <><path d="M21.3 8.7l-5.6-5.6a1 1 0 0 0-1.4 0L2.7 14.3a1 1 0 0 0 0 1.4l5.6 5.6a1 1 0 0 0 1.4 0L21.3 10.1a1 1 0 0 0 0-1.4z" /></>,
    globe: <><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></>,
    mosque: <><path d="M4 22V11a8 8 0 0 1 16 0v11" /><path d="M12 3c0 1.5 2 2 2 4" /><line x1="2" y1="22" x2="22" y2="22" /><path d="M9 22v-4a3 3 0 0 1 6 0v4" /></>,
    star: <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />,
    book: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></>,
    arrowRight: <><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></>,
    arrowLeft: <><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></>,
    cake: <><path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8" /><path d="M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2.5 2 4 2 2-1 2-1" /><path d="M2 21h20" /><path d="M7 8v3M12 8v3M17 8v3" /></>,
    baby: <><path d="M9 12h.01M15 12h.01M10 16c.5.3 1.2.5 2 .5s1.5-.2 2-.5" /><path d="M19 6.3a9 9 0 0 1 1.8 3.9 2 2 0 0 1 0 3.6 9 9 0 0 1-17.6 0 2 2 0 0 1 0-3.6A9 9 0 0 1 12 3c2 0 3.5 1.1 3.5 2.5s-.9 2.5-2 2.5c-.8 0-1.5-.4-1.5-1" /></>,
    ban: <><circle cx="12" cy="12" r="10" /><line x1="4.9" y1="4.9" x2="19.1" y2="19.1" /></>,
    check: <polyline points="20 6 9 17 4 12" />,
    clipboard: <><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" /><line x1="9" y1="12" x2="15" y2="12" /><line x1="9" y1="16" x2="13" y2="16" /></>,
    badge: <><path d="M12 2l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 7.7l5.4-.8z" /></>,
    shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></>,
    trash: <><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /></>,
    clock: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>,
};

const TABS = [
    { id: 'perso', label: 'Personnel', icon: icons.user },
    { id: 'localisation', label: 'Localisation', icon: icons.pin },
    { id: 'religion', label: 'Religion', icon: icons.mosque },
    { id: 'profession', label: 'Profession', icon: icons.briefcase },
];

// Dictionnaires pour afficher des libellés lisibles dans le résumé
const LABELS = {
    marital_status: { celibataire: 'Célibataire', marie: 'Marié(e)', divorce: 'Divorcé(e)', veuf: 'Veuf/Veuve' },
    pratique_religieuse: { peu_pratiquant: 'Peu pratiquant(e)', pratiquant: 'Pratiquant(e)', tres_pratiquant: 'Très pratiquant(e)' },
    priere: { '5_fois': 'Les 5 prières', parfois: 'Parfois', rarement: 'Rarement', jamais: 'Pas encore' },
    porte_voile: { hijab: 'Hijab', khimar: 'Khimar', jilbab: 'Jilbab', abaya: 'Abaya', niqab: 'Niqab', sitar: 'Sitar', pudique_sans_voile: 'Vêtements pudiques', non: 'Ne porte pas encore' },
    barbe: { oui: 'Oui', non: 'Non' },
    ecole_pensee: { hanafi: 'Hanafi', maliki: 'Maliki', shafii: 'Shafi\'i', hanbali: 'Hanbali', sans: 'Sans école particulière' },
    converti: { oui: 'Oui', non: 'Non, de naissance' },
};
const lisible = (champ, valeur) => (LABELS[champ] && LABELS[champ][valeur]) || valeur;

const Profile = () => {
    const { user, refreshStatus } = useAuth();
    const [resoumsg, setResoumsg] = useState('');
    const [resoumLoading, setResoumLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('perso');
    const [vue, setVue] = useState('formulaire'); // 'formulaire' ou 'presentation'
    const [form, setForm] = useState({
        first_name: '', age: '', city: '', profession: '',
        education: '', marital_status: '', children: 0, bio: '',
        pays_origine: '', pays_residence: '', langues: '',
        taille: '', poids: '', nombre_epouse: 0,
        pratique_religieuse: '', priere: '', porte_voile: '', barbe: '', ecole_pensee: '', converti: '',
        date_naissance: '', enfant_souhaite: '', ethnie: '', criteres_redhibitoires: '',
    });

    // ═══ Onglet Tuteur : état séparé du reste du formulaire de profil ═══
    const [guardians, setGuardians] = useState([]);
    const [guardiansLoading, setGuardiansLoading] = useState(false);
    const [tuteurForm, setTuteurForm] = useState({ name: '', email: '', relation: '' });
    const [tuteurSending, setTuteurSending] = useState(false);
    const [tuteurStatus, setTuteurStatus] = useState('');

    const { isSmall, isMobile, isTablet } = useResponsiveLocal();
    const compact = isMobile || isTablet;

    useEffect(() => { fetchProfile(); }, []);

    // Tant que le profil n'est pas validé, on interroge le statut toutes les 10s.
    // Dès que l'admin valide, le membre est redirigé automatiquement (via les gardes).
    useEffect(() => {
        if (!user || user.statut_profil === 'valide') return;
        const id = setInterval(() => { refreshStatus(); }, 10000);
        return () => clearInterval(id);
    }, [user]);
    useEffect(() => { fetchGuardians(); }, []);
    useEffect(() => {
        if (success || error) {
            const timer = setTimeout(() => { setSuccess(''); setError(''); }, 5000);
            return () => clearTimeout(timer);
        }
    }, [success, error]);

    const fetchProfile = async () => {
        try {
            const response = await api.get(`/profiles/${user.id}`);
            if (response.data) setForm({ ...form, ...response.data });
        } catch (err) { console.error(err); }
    };

    // ⚠️ Suppose une route backend GET /api/guardian/mine (voir guardianController.js)
    const fetchGuardians = async () => {
        setGuardiansLoading(true);
        try {
            const response = await api.get('/guardian/mine');
            setGuardians(response.data || []);
        } catch (err) {
            console.error('Erreur chargement tuteurs:', err);
        } finally {
            setGuardiansLoading(false);
        }
    };

    const handleTuteurChange = (e) => setTuteurForm({ ...tuteurForm, [e.target.name]: e.target.value });

    // ⚠️ Suppose une route backend POST /api/guardian/invite
    const handleInviteGuardian = async (e) => {
        e.preventDefault();
        if (!tuteurForm.email) return;
        setTuteurSending(true);
        setTuteurStatus('');
        try {
            await api.post('/guardian/invite', tuteurForm);
            setTuteurStatus('success');
            setTuteurForm({ name: '', email: '', relation: '' });
            fetchGuardians();
        } catch (err) {
            console.error('Erreur invitation tuteur:', err);
            setTuteurStatus('error');
        } finally {
            setTuteurSending(false);
        }
    };

    // ⚠️ Suppose une route backend DELETE /api/guardian/:id
    const handleRevokeGuardian = async (guardianId) => {
        if (!window.confirm('Révoquer l\'accès de ce tuteur ?')) return;
        try {
            await api.delete(`/guardian/${guardianId}`);
            fetchGuardians();
        } catch (err) {
            console.error('Erreur révocation tuteur:', err);
        }
    };

    const calculerAge = (dateStr) => {
        if (!dateStr) return '';
        const naissance = new Date(dateStr);
        const today = new Date();
        let age = today.getFullYear() - naissance.getFullYear();
        const m = today.getMonth() - naissance.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < naissance.getDate())) age--;
        return age;
    };

    // Calcule le pourcentage de complétude du profil
    const calculerCompletude = () => {
        const champs = [
            'first_name', 'date_naissance', 'taille', 'poids', 'marital_status',
            'children', 'enfant_souhaite', 'langues',
            'city', 'pays_origine', 'pays_residence', 'ethnie',
            'pratique_religieuse', 'priere', 'ecole_pensee', 'converti',
            'profession', 'education', 'bio', 'criteres_redhibitoires',
            'photo_url'
        ];

        if (user?.gender === 'femme') {
            champs.push('porte_voile');
        }
        if (user?.gender === 'homme') {
            champs.push('barbe');
            if (form.marital_status === 'marie') {
                champs.push('nombre_epouse');
            }
        }

        const remplis = champs.filter(c => {
            const val = form[c];
            if (val === '' || val === null || val === undefined) return false;
            // Pour les champs numériques, 0 est une réponse valide
            if (val === 0 && (c === 'children' || c === 'enfant_souhaite' || c === 'nombre_epouse')) return true;
            if (val === 0) return false;
            return true;
        }).length;

        return Math.round((remplis / champs.length) * 100);
    };

    // Champs par onglet (pour le pourcentage de chaque menu)
    const champsParOnglet = () => {
        const perso = ['first_name', 'date_naissance', 'taille', 'poids', 'marital_status', 'children', 'enfant_souhaite', 'langues'];
        const localisation = ['city', 'pays_origine', 'pays_residence', 'ethnie'];
        const religion = ['pratique_religieuse', 'priere', 'ecole_pensee', 'converti'];
        const profession = ['profession', 'education', 'bio', 'criteres_redhibitoires'];

        if (user?.gender === 'homme') {
            religion.push('barbe');
            if (form.marital_status === 'marie') perso.push('nombre_epouse');
        }
        if (user?.gender === 'femme') {
            religion.push('porte_voile');
        }
        return { perso, localisation, religion, profession };
    };

    // Pourcentage rempli pour une liste de champs donnée
    const pourcentageChamps = (liste) => {
        const remplis = liste.filter(c => {
            const val = form[c];
            if (val === '' || val === null || val === undefined) return false;
            if (val === 0 && (c === 'children' || c === 'enfant_souhaite' || c === 'nombre_epouse')) return true;
            if (val === 0) return false;
            return true;
        }).length;
        return Math.round((remplis / liste.length) * 100);
    };

    const completudeOnglets = () => {
        const groupes = champsParOnglet();
        return {
            perso: pourcentageChamps(groupes.perso),
            localisation: pourcentageChamps(groupes.localisation),
            religion: pourcentageChamps(groupes.religion),
            profession: pourcentageChamps(groupes.profession),
        };
    };

    const pctOnglets = completudeOnglets();

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        // L'avatar est obligatoire
        if (!form.photo_url) {
            setError('Veuillez choisir un avatar avant d\'enregistrer votre profil.');
            setActiveTab('perso'); // l'avatar est en haut, on ramène au début
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        setLoading(true); setError(''); setSuccess('');
        try {
            await api.put('/profiles/me', form);
            setSuccess('Profil mis à jour avec succès !');
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur lors de la mise à jour');
        } finally { setLoading(false); }
    };

    const currentIndex = TABS.findIndex(t => t.id === activeTab);
    const isLastTab = currentIndex === TABS.length - 1;
    const goNext = () => { if (!isLastTab) setActiveTab(TABS[currentIndex + 1].id); };
    const goPrev = () => { if (currentIndex > 0) setActiveTab(TABS[currentIndex - 1].id); };

    const layoutStyle = { display: 'flex', gap: compact ? '0' : '28px', flexDirection: compact ? 'column' : 'row', padding: isSmall ? '12px' : isMobile ? '16px' : '32px', maxWidth: '1320px', margin: '0 auto', boxSizing: 'border-box' };
    const gridStyle = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 1fr' : '1fr 1fr', gap: isSmall ? '12px' : isMobile ? '16px' : '20px 28px' };

    // Formatage de date robuste : certains navigateurs mobiles (Huawei, anciens Chromium)
    // plantent sur toLocaleDateString avec { month: 'long' }. On tente le format localisé,
    // et en cas d'échec on retombe sur un format simple jj/mm/aaaa, sans jamais crasher.
    const formatDateSure = (valeur) => {
        try {
            const d = new Date(valeur);
            if (isNaN(d.getTime())) return '';
            try {
                return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
            } catch (e) {
                const jj = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                return `${jj}/${mm}/${d.getFullYear()}`;
            }
        } catch (e) {
            return '';
        }
    };
    const tabsBarStyle = { ...styles.tabsBar, padding: isSmall ? '4px' : '4px 8px', overflowX: isMobile ? 'auto' : 'visible', justifyContent: isMobile ? 'flex-start' : 'space-between' };
    const vueBarStyle = { ...styles.vueBar, flexDirection: isSmall ? 'column' : 'row' };

    const Label = ({ icon, children }) => (
        <label style={styles.label}><span style={styles.labelIcon}><Icon path={icon} size={16} /></span>{children}</label>
    );

    // ═══ Ligne d'information dans les cartes de la fiche de présentation ═══
    const InfoLigne = ({ label, val }) => (
        <div style={styles.infoLigne}>
            <span style={styles.infoLabel}>{label}</span>
            <span style={styles.infoVal}>{val && String(val).trim() !== '' ? val : 'Non renseigné'}</span>
        </div>
    );

    // ═══ Badge affiché à droite de chaque onglet (pourcentage de complétude) ═══
    const TabBadge = ({ tab }) => {
        return pctOnglets[tab.id] === 100 ? (
            <span style={styles.tabCheckCircle}>
                <Icon path={icons.check} size={11} color="#fff" />
            </span>
        ) : (
            <span style={{ ...styles.tabPct, color: activeTab === tab.id ? 'rgba(255,255,255,0.85)' : '#999' }}>
                {pctOnglets[tab.id]}%
            </span>
        );
    };

    const handleResoumettre = async () => {
        // L'avatar est obligatoire, comme pour l'enregistrement normal
        if (!form.photo_url) {
            setResoumsg('Veuillez choisir un avatar avant de renvoyer votre profil.');
            setActiveTab('perso');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        setResoumLoading(true);
        setResoumsg('');
        try {
            // 1) On enregistre d'abord les corrections du profil…
            await api.put('/profiles/me', form);
            // 2) …puis on renvoie à l'examen (refuse -> en_attente)
            await api.put('/profiles/resoumettre');
            await refreshStatus();
            setResoumsg('Votre profil corrigé a été renvoyé à l\'examen.');
        } catch (err) {
            setResoumsg(err.response?.data?.message || 'Erreur lors du renvoi.');
        } finally {
            setResoumLoading(false);
        }
    };

    return (
        <div style={styles.page}>
            <NavbarAuth myPhoto={form.photo_url} />

            {user && user.statut_profil === 'brouillon' && (
                <div style={styles.bandeauBrouillon}>
                    <div style={{ fontWeight: 700, marginBottom: '4px' }}>
                        Votre profil n'est pas encore soumis
                    </div>
                    <div>
                        Complétez votre profil ci-dessous et enregistrez-le. Vous pourrez ensuite
                        l'envoyer à l'examen de notre équipe pour validation.
                    </div>
                    {user.profil_existe && (
                        <>
                            <button
                                onClick={handleResoumettre}
                                disabled={resoumLoading}
                                style={styles.btnResoumettre}
                            >
                                {resoumLoading ? 'Envoi…' : 'Envoyer à l\'examen'}
                            </button>
                            {resoumsg && <div style={{ marginTop: '8px', fontWeight: 600 }}>{resoumsg}</div>}
                        </>
                    )}
                </div>
            )}

            {user && user.statut_profil === 'en_attente' && (
                <div style={styles.bandeauAttente}>
                    ⏳ Votre profil est en attente de validation par notre équipe.
                    Vous pourrez accéder au site dès qu'il sera validé.
                </div>
            )}
            {user && user.statut_profil === 'refuse' && (
                <div style={styles.bandeauRefus}>
                    <div style={{ fontWeight: 700, marginBottom: '6px' }}>
                        Votre profil doit être corrigé
                    </div>
                    {user.motif_refus_profil && (
                        <div style={styles.bandeauMotif}>
                            Motif : {user.motif_refus_profil}
                        </div>
                    )}
                    <div style={{ marginTop: '4px' }}>
                        Corrigez votre profil ci-dessous, puis cliquez sur « Renvoyer à l'examen » : vos modifications seront enregistrées et envoyées automatiquement.
                    </div>
                    <button
                        onClick={handleResoumettre}
                        disabled={resoumLoading}
                        style={styles.btnResoumettre}
                    >
                        {resoumLoading ? 'Envoi…' : 'Renvoyer à l\'examen'}
                    </button>
                    {resoumsg && <div style={{ marginTop: '8px', fontWeight: 600 }}>{resoumsg}</div>}
                </div>
            )}

            <div style={layoutStyle}>
                <main style={{
                    ...styles.card,
                    padding: isSmall ? '16px' : isMobile ? '20px' : isTablet ? '28px' : '40px',
                    ...(vue === 'presentation' ? { background: '#faf7f2' } : {}),
                }}>
                    {/* Boutons de bascule de vue */}
                    <div style={vueBarStyle}>
                        <button
                            onClick={() => setVue('formulaire')}
                            style={vue === 'formulaire' ? styles.vueBtnActive : styles.vueBtn}
                        >
                            <Icon path={icons.user} size={17} color={vue === 'formulaire' ? '#fff' : '#8b1a4a'} /> Mon Profil
                        </button>
                        <button
                            onClick={() => setVue('presentation')}
                            style={vue === 'presentation' ? styles.vueBtnActive : styles.vueBtn}
                        >
                            <Icon path={icons.clipboard} size={17} color={vue === 'presentation' ? '#fff' : '#c0306a'} /> Présentation du profil
                        </button>
                        <button
                            onClick={() => setVue('tuteur')}
                            style={vue === 'tuteur' ? styles.vueBtnActive : styles.vueBtn}
                        >
                            <Icon path={icons.shield} size={17} color={vue === 'tuteur' ? '#fff' : '#c0306a'} /> Tuteur
                            {guardians.filter(g => g.status === 'active').length > 0 && (
                                <span style={styles.vueBtnBadge}>{guardians.filter(g => g.status === 'active').length}</span>
                            )}
                        </button>
                    </div>

                    {vue === 'formulaire' && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
                            <h2 style={{ ...styles.title, margin: 0, fontSize: isSmall ? '22px' : isMobile ? '25px' : '30px' }}>
                                <span style={styles.titleIcon}><Icon path={icons.user} size={isSmall ? 20 : 26} color="#4a0028" /></span>
                                Mon Profil
                            </h2>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                <div
                                    style={{ ...styles.idBadge, fontSize: isSmall ? '12px' : '13px', padding: isSmall ? '8px 12px' : '10px 16px' }}
                                    title="Votre numéro de membre, à communiquer en cas de besoin d'assistance"
                                >
                                    <Icon path={icons.badge} size={14} color="#c0306a" />
                                    ID :<strong style={{ marginLeft: '2px' }}>#{form.numero_membre ?? user?.numero_membre ?? '—'}</strong>
                                </div>
                                <div style={{ ...styles.completudeBadge, fontSize: isSmall ? '12px' : '14px', padding: isSmall ? '8px 14px' : '10px 18px' }}>
                                    Profil complété à <strong style={{ fontSize: isSmall ? '16px' : '20px', marginLeft: '4px' }}>{calculerCompletude()}%</strong>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Choix de l'avatar (uniquement en vue formulaire) */}
                    {vue === 'formulaire' && (
                        <div style={styles.avatarSection}>
                            <div style={styles.avatarHead}>
                                <h3 style={styles.photoHeading}>Choisissez votre avatar</h3>
                                <p style={styles.photoDesc}>
                                    Sélectionnez l'avatar qui vous représentera auprès des autres membres.
                                    Ce choix est obligatoire.
                                </p>
                            </div>
                            <div style={styles.avatarGrid}>
                                {AVATARS.filter(a => a.genre === user?.gender).map(av => (
                                    <button
                                        key={av.id}
                                        type="button"
                                        onClick={() => setForm({ ...form, photo_url: av.src })}
                                        style={form.photo_url === av.src ? styles.avatarChoiceActive : styles.avatarChoice}
                                        title="Choisir cet avatar"
                                    >
                                        <img src={av.src} alt={av.id} style={styles.avatarImg} />
                                        {form.photo_url === av.src && (
                                            <span style={styles.avatarCheck}>
                                                <Icon path={icons.check} size={14} color="#fff" />
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {success && <div style={styles.success}>{success}</div>}
                    {error && <div style={styles.error}>{error}</div>}

                    {/* ═══ VUE FORMULAIRE ═══ */}
                    {vue === 'formulaire' && (
                        <>
                            {/* ONGLETS */}
                            <div style={tabsBarStyle}>
                                {TABS.map((tab, i) => (
                                    <React.Fragment key={tab.id}>
                                        <button
                                            onClick={() => setActiveTab(tab.id)}
                                            style={{
                                                ...(activeTab === tab.id ? styles.tabActive : styles.tab),
                                                padding: isSmall ? '8px 10px' : '10px 16px',
                                                fontSize: isSmall ? '10px' : '12px',
                                            }}
                                        >
                                            <div style={styles.tabTop}>
                                                <Icon path={tab.icon} size={isSmall ? 16 : 20} color={activeTab === tab.id ? '#fff' : '#c0306a'} />
                                                <span>{tab.label}</span>
                                            </div>
                                            <TabBadge tab={tab} />
                                        </button>
                                        {!isMobile && i < TABS.length - 1 && <span style={styles.stepLine} />}
                                    </React.Fragment>
                                ))}
                            </div>

                            <form onSubmit={handleSubmit} onKeyDown={(e) => {
                                // Empêche la soumission accidentelle du formulaire via la touche Entrée
                                // (sauf dans les <textarea>, où Entrée sert à sauter une ligne).
                                if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                                    e.preventDefault();
                                }
                            }}>
                                {/* ONGLET PERSONNEL */}
                                <div style={{ display: activeTab === 'perso' ? 'block' : 'none' }}>
                                    <div style={gridStyle}>
                                        <div style={styles.field}>
                                            <Label icon={icons.user}>Kounya</Label>
                                            <input name="first_name" value={form.first_name} onChange={handleChange} style={styles.input} placeholder="Votre kounya" required />
                                        </div>
                                        <div style={styles.field}>
                                            <Label icon={icons.cake}>Date de naissance</Label>
                                            <input name="date_naissance" type="date" value={form.date_naissance ? form.date_naissance.slice(0, 10) : ''} onChange={handleChange} style={styles.input} />
                                            {form.date_naissance && (
                                                <span style={{ color: '#9a7d88', fontSize: '12px', marginTop: '4px' }}>
                                                    Âge calculé : {calculerAge(form.date_naissance)} ans
                                                </span>
                                            )}
                                        </div>
                                        <div style={styles.field}>
                                            <Label icon={icons.ruler}>Taille (cm)</Label>
                                            <input name="taille" type="number" value={form.taille || ''} onChange={handleChange} style={styles.input} placeholder="Ex : 170" min="100" max="250" />
                                        </div>
                                        <div style={styles.field}>
                                            <Label icon={icons.ruler}>Poids (kg)</Label>
                                            <input name="poids" type="number" value={form.poids || ''} onChange={handleChange} style={styles.input} placeholder="Ex : 65" min="30" max="250" />
                                        </div>
                                        <div style={styles.field}>
                                            <Label icon={icons.heart}>Situation matrimoniale</Label>
                                            <select name="marital_status" value={form.marital_status} onChange={handleChange} style={styles.input}>
                                                <option value="">Choisir...</option>
                                                <option value="celibataire">Célibataire</option>
                                                {user?.gender !== 'femme' && <option value="marie">Marié(e)</option>}
                                                <option value="divorce">Divorcé(e)</option>
                                                <option value="veuf">Veuf/Veuve</option>
                                            </select>
                                        </div>
                                        <div style={styles.field}>
                                            <Label icon={icons.users}>Nombre d'enfants actuels</Label>
                                            <input name="children" type="number" value={form.children} onChange={handleChange} style={styles.input} min="0" max="20" />
                                        </div>
                                        <div style={styles.field}>
                                            <Label icon={icons.baby}>Nombre d'enfants souhaités</Label>
                                            <input name="enfant_souhaite" type="number" value={form.enfant_souhaite || ''} onChange={handleChange} style={styles.input} placeholder="Ex : 3" min="0" max="20" />
                                        </div>
                                        {user?.gender === 'homme' && form.marital_status === 'marie' && (
                                            <div style={styles.field}>
                                                <Label icon={icons.users}>Nombre d'épouses</Label>
                                                <input name="nombre_epouse" type="number" value={form.nombre_epouse || 0} onChange={handleChange} style={styles.input} min="0" max="4" />
                                            </div>
                                        )}
                                        <div style={{ ...styles.field, gridColumn: isMobile ? 'auto' : '1 / -1' }}>
                                            <Label icon={icons.mail}>Langues parlées</Label>
                                            <SelectAvecAutre
                                                name="langues"
                                                value={form.langues || ''}
                                                onChange={handleChange}
                                                style={styles.input}
                                                options={[{ value: '', label: 'Choisir...' }, ...LANGUES.map(l => ({ value: l, label: l }))]}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* ONGLET LOCALISATION */}
                                <div style={{ display: activeTab === 'localisation' ? 'block' : 'none' }}>
                                    <div style={gridStyle}>
                                        <div style={styles.field}>
                                            <Label icon={icons.building}>Ville</Label>
                                            <input name="city" value={form.city} onChange={handleChange} style={styles.input} placeholder="Votre ville" />
                                        </div>
                                        <div style={styles.field}>
                                            <Label icon={icons.globe}>Pays d'origine</Label>
                                            <select name="pays_origine" value={form.pays_origine || ''} onChange={handleChange} style={styles.input}>
                                                <option value="">Choisir...</option>
                                                {PAYS.map((p, i) => <option key={i} value={p}>{p}</option>)}
                                            </select>
                                        </div>
                                        <div style={styles.field}>
                                            <Label icon={icons.home}>Pays de résidence</Label>
                                            <select name="pays_residence" value={form.pays_residence || ''} onChange={handleChange} style={styles.input}>
                                                <option value="">Choisir...</option>
                                                {PAYS.map((p, i) => <option key={i} value={p}>{p}</option>)}
                                            </select>
                                        </div>
                                        <div style={{ ...styles.field, gridColumn: isMobile ? 'auto' : '1 / -1' }}>
                                            <Label icon={icons.globe}>Ethnie / Origine ethnique</Label>
                                            <SelectAvecAutre
                                                name="ethnie"
                                                value={form.ethnie || ''}
                                                onChange={handleChange}
                                                style={styles.input}
                                                options={[{ value: '', label: 'Choisir...' }, ...ETHNIES.map(e => ({ value: e, label: e }))]}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* ONGLET RELIGION */}
                                <div style={{ display: activeTab === 'religion' ? 'block' : 'none' }}>
                                    <div style={gridStyle}>
                                        <div style={styles.field}>
                                            <Label icon={icons.mosque}>Pratique religieuse</Label>
                                            <select name="pratique_religieuse" value={form.pratique_religieuse || ''} onChange={handleChange} style={styles.input}>
                                                <option value="">Choisir...</option>
                                                <option value="peu_pratiquant">Peu pratiquant(e)</option>
                                                <option value="pratiquant">Pratiquant(e)</option>
                                                <option value="tres_pratiquant">Très pratiquant(e)</option>
                                            </select>
                                        </div>
                                        <div style={styles.field}>
                                            <Label icon={icons.star}>Prière</Label>
                                            <select name="priere" value={form.priere || ''} onChange={handleChange} style={styles.input}>
                                                <option value="">Choisir...</option>
                                                <option value="5_fois">Les 5 prières</option>
                                                <option value="parfois">Parfois</option>
                                                <option value="rarement">Rarement</option>
                                                <option value="jamais">Pas encore</option>
                                            </select>
                                        </div>
                                        {user?.gender === 'femme' && (
                                            <div style={styles.field}>
                                                <Label icon={icons.star}>Tenue vestimentaire</Label>
                                                <select name="porte_voile" value={form.porte_voile || ''} onChange={handleChange} style={styles.input}>
                                                    <option value="">Choisir...</option>
                                                    <option value="hijab">Hijab</option>
                                                    <option value="khimar">Khimar</option>
                                                    <option value="jilbab">Jilbab</option>
                                                    <option value="abaya">Abaya</option>
                                                    <option value="niqab">Niqab</option>
                                                    <option value="sitar">Sitar / Gants + Niqab</option>
                                                    <option value="pudique_sans_voile">Vêtements pudiques sans voile</option>
                                                    <option value="non">Ne porte pas encore le voile</option>
                                                </select>
                                            </div>
                                        )}
                                        {user?.gender === 'homme' && (
                                            <div style={styles.field}>
                                                <Label icon={icons.star}>Barbe</Label>
                                                <select name="barbe" value={form.barbe || ''} onChange={handleChange} style={styles.input}>
                                                    <option value="">Choisir...</option>
                                                    <option value="oui">Oui</option>
                                                    <option value="non">Non</option>
                                                </select>
                                            </div>
                                        )}
                                        <div style={styles.field}>
                                            <Label icon={icons.book}>École de pensée</Label>
                                            <select name="ecole_pensee" value={form.ecole_pensee || ''} onChange={handleChange} style={styles.input}>
                                                <option value="">Choisir...</option>
                                                <option value="hanafi">Hanafi</option>
                                                <option value="maliki">Maliki</option>
                                                <option value="shafii">Shafi'i</option>
                                                <option value="hanbali">Hanbali</option>
                                                <option value="sans">Sans école particulière</option>
                                            </select>
                                        </div>
                                        <div style={styles.field}>
                                            <Label icon={icons.heart}>Converti(e) à l'Islam</Label>
                                            <select name="converti" value={form.converti || ''} onChange={handleChange} style={styles.input}>
                                                <option value="">Choisir...</option>
                                                <option value="oui">Oui</option>
                                                <option value="non">Non, de naissance</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* ONGLET PROFESSION */}
                                <div style={{ display: activeTab === 'profession' ? 'block' : 'none' }}>
                                    <ErrorBoundary>
                                        <div style={gridStyle}>
                                            <div style={styles.field}>
                                                <Label icon={icons.briefcase}>Profession</Label>
                                                <input name="profession" value={form.profession} onChange={handleChange} style={styles.input} placeholder="Votre profession" />
                                            </div>
                                            <div style={styles.field}>
                                                <Label icon={icons.cap}>Niveau d'études</Label>
                                                <SelectAvecAutre
                                                    name="education"
                                                    value={form.education}
                                                    onChange={handleChange}
                                                    style={styles.input}
                                                    options={['', 'Baccalauréat', 'Licence', 'Master', 'Doctorat', 'Autre'].map(v => ({ value: v, label: v === '' ? 'Choisir...' : v }))}
                                                />
                                            </div>
                                        </div>
                                        <div style={{ ...styles.field, marginTop: '20px' }}>
                                            <Label icon={icons.edit}>Présentation</Label>
                                            <textarea name="bio" value={form.bio} onChange={handleChange} style={styles.textarea} rows="5" placeholder="Parlez de vous, de vos valeurs, de ce que vous recherchez..." />
                                        </div>
                                        <div style={{ ...styles.field, marginTop: '20px' }}>
                                            <Label icon={icons.ban}>Critères rédhibitoires</Label>
                                            <textarea name="criteres_redhibitoires" value={form.criteres_redhibitoires || ''} onChange={handleChange} style={styles.textarea} rows="3" placeholder="Ce que vous n'acceptez pas chez un futur conjoint (ex : ne prie pas, fumeur, ne souhaite pas d'enfants...)" />
                                        </div>
                                        {form.created_at && (
                                            <div style={styles.registeredBox}>
                                                <Icon path={icons.calendar} size={15} color="#9a7d88" />
                                                <span>Membre depuis le {formatDateSure(form.created_at)}</span>
                                            </div>
                                        )}
                                    </ErrorBoundary>
                                </div>

                                {/* NAVIGATION */}
                                <div style={styles.navBtns}>
                                    {currentIndex > 0 && (
                                        <button type="button" onClick={goPrev} style={styles.prevBtn}>
                                            <Icon path={icons.arrowLeft} size={16} color="#f3e0c7" /> Précédent
                                        </button>
                                    )}
                                    <div style={{ flex: 1 }} />
                                    {!isLastTab ? (
                                        <button type="button" onClick={goNext} style={styles.nextBtn}>
                                            Suivant <Icon path={icons.arrowRight} size={16} color="#f3e0c7" />
                                        </button>
                                    ) : (
                                        <button type="button" onClick={handleSubmit} style={styles.submitBtn} disabled={loading}>
                                            <Icon path={icons.save} size={18} color="#f3e0c7" />
                                            {loading ? 'Enregistrement...' : 'Enregistrer le profil'}
                                        </button>
                                    )}
                                </div>
                            </form>
                        </>
                    )}

                    {/* ═══ VUE TUTEUR ═══ */}
                    {vue === 'tuteur' && (
                        <div style={styles.tuteurWrap}>
                            <div style={styles.tuteurIntro}>
                                <Icon path={icons.shield} size={22} color="#c0306a" />
                                <div>
                                    <h3 style={{ margin: '0 0 4px', color: '#4a0028', fontSize: '17px', fontWeight: '700' }}>
                                        Superviseur de mon profil
                                    </h3>
                                    <p style={{ margin: 0, color: '#9a7d88', fontSize: '13px', lineHeight: '1.6' }}>
                                        Ajoutez un parent ou un tuteur : il recevra par email une copie
                                        des messages que vous envoyez et recevez. C'est totalement optionnel.
                                    </p>
                                </div>
                            </div>

                            <div style={{ ...gridStyle, marginTop: '20px' }}>
                                <div style={styles.field}>
                                    <Label icon={icons.user}>Nom (optionnel)</Label>
                                    <input name="name" value={tuteurForm.name} onChange={handleTuteurChange} style={styles.input} placeholder="Ex : Ahmed Benali" />
                                </div>
                                <div style={styles.field}>
                                    <Label icon={icons.mail}>Email du tuteur</Label>
                                    <input name="email" type="email" value={tuteurForm.email} onChange={handleTuteurChange} style={styles.input} placeholder="email@exemple.com" required />
                                </div>
                                <div style={styles.field}>
                                    <Label icon={icons.users}>Lien de parenté</Label>
                                    <SelectAvecAutre
                                        name="relation"
                                        value={tuteurForm.relation}
                                        onChange={handleTuteurChange}
                                        style={styles.input}
                                        options={[
                                            { value: '', label: 'Choisir...' },
                                            { value: 'père', label: 'Père' },
                                            { value: 'mère', label: 'Mère' },
                                            { value: 'frère', label: 'Frère' },
                                            { value: 'sœur', label: 'Sœur' },
                                            { value: 'wali', label: 'Wali' },
                                            { value: 'autre', label: 'Autre' },
                                        ]}
                                        autreLabels={['autre', 'Autre']}
                                    />
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={handleInviteGuardian}
                                disabled={tuteurSending}
                                style={{ ...styles.nextBtn, marginTop: '18px', opacity: tuteurSending ? 0.7 : 1 }}
                            >
                                <Icon path={icons.mail} size={16} color="#f3e0c7" />
                                {tuteurSending ? 'Ajout...' : 'Ajouter ce tuteur'}
                            </button>

                            {tuteurStatus === 'success' && (
                                <div style={{ ...styles.success, marginTop: '16px' }}>Tuteur ajouté. Un email de confirmation lui a été envoyé.</div>
                            )}
                            {tuteurStatus === 'error' && (
                                <div style={{ ...styles.error, marginTop: '16px' }}>Une erreur est survenue. Réessayez.</div>
                            )}

                            <div style={{ marginTop: '28px' }}>
                                <h4 style={{ color: '#4a0028', fontSize: '14px', fontWeight: '700', marginBottom: '12px' }}>
                                    Tuteurs ajoutés
                                </h4>
                                {guardiansLoading ? (
                                    <p style={{ color: '#9a7d88', fontSize: '13px' }}>Chargement...</p>
                                ) : guardians.length === 0 ? (
                                    <p style={{ color: '#9a7d88', fontSize: '13px' }}>Aucun tuteur ajouté pour le moment.</p>
                                ) : (
                                    guardians.map(g => (
                                        <div key={g.id} style={styles.tuteurRow}>
                                            <div>
                                                <div style={{ color: '#4a0028', fontSize: '13.5px', fontWeight: '700' }}>
                                                    {g.guardian_name || g.guardian_email}
                                                    {g.relation && <span style={{ color: '#9a7d88', fontWeight: '400' }}> ({g.relation})</span>}
                                                </div>
                                                <div style={{ color: '#9a7d88', fontSize: '12px', marginTop: '2px' }}>{g.guardian_email}</div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                                {g.status === 'active' ? (
                                                    <span style={styles.tuteurStatusActive}>
                                                        <Icon path={icons.check} size={12} color="#c0306a" /> Actif
                                                    </span>
                                                ) : (
                                                    <span style={styles.tuteurStatusPending}>
                                                        <Icon path={icons.clock} size={12} color="#b8860b" /> En attente
                                                    </span>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRevokeGuardian(g.id)}
                                                    style={styles.tuteurRevokeBtn}
                                                    aria-label="Révoquer"
                                                >
                                                    <Icon path={icons.trash} size={15} color="#c0306a" />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                    {vue === 'presentation' && (
                        <div style={{ ...styles.profileLayout, gridTemplateColumns: compact ? '1fr' : '300px 1fr' }}>

                            {/* ═══ COLONNE GAUCHE : carte photo ═══ */}
                            <div style={styles.profileSideCard}>
                                <div style={styles.profilePhotoBox}>
                                    {form.photo_url ? (
                                        <img src={form.photo_url} alt="profil" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} />
                                    ) : (
                                        <Icon path={icons.user} size={40} color="#c0306a" />
                                    )}
                                </div>

                                <div style={styles.profileSideBody}>
                                    <h3 style={styles.profileSideName}>
                                        {form.first_name || '—'}
                                        {form.date_naissance && <span>, {calculerAge(form.date_naissance)}</span>}
                                    </h3>
                                    <p style={styles.profileSideLoc}>
                                        <Icon path={icons.pin} size={13} color="#9a7d88" />
                                        {[form.city, form.pays_residence].filter(Boolean).join(', ') || 'Non renseigné'}
                                    </p>

                                    <div style={styles.apercuBox}>
                                        <Icon path={icons.clipboard} size={14} color="#8b1a4a" />
                                        <span>Voici comment les autres membres voient votre profil.</span>
                                    </div>

                                    <button onClick={() => setVue('formulaire')} style={styles.editProfileBtn}>
                                        <Icon path={icons.edit} size={14} color="#f3e0c7" /> Modifier mon profil
                                    </button>
                                </div>
                            </div>

                            {/* ═══ COLONNE DROITE : informations ═══ */}
                            <div style={styles.profileMainCol}>

                                {/* À propos de moi */}
                                <div style={styles.infoCard}>
                                    <h4 style={styles.infoCardTitle}>
                                        <Icon path={icons.edit} size={15} color="#c0306a" /> À propos de moi
                                    </h4>
                                    <p style={styles.aboutText}>
                                        {form.bio || 'Aucune présentation renseignée.'}
                                    </p>
                                </div>

                                {/* Critères rédhibitoires */}
                                {form.criteres_redhibitoires && (
                                    <div style={styles.infoCard}>
                                        <h4 style={styles.infoCardTitle}>
                                            <Icon path={icons.ban} size={15} color="#c0306a" /> Critères rédhibitoires
                                        </h4>
                                        <p style={styles.aboutText}>{form.criteres_redhibitoires}</p>
                                    </div>
                                )}

                                {/* Grille de cartes d'informations */}
                                <div style={{ ...styles.infoGrid, gridTemplateColumns: compact ? '1fr' : '1fr 1fr' }}>

                                    <div style={styles.infoCard}>
                                        <h4 style={styles.infoCardTitle}>
                                            <Icon path={icons.mosque} size={15} color="#c0306a" /> Pratique religieuse
                                        </h4>
                                        <InfoLigne label="Niveau" val={lisible('pratique_religieuse', form.pratique_religieuse)} />
                                        <InfoLigne label="Prière" val={lisible('priere', form.priere)} />
                                        <InfoLigne label="Madhhab" val={lisible('ecole_pensee', form.ecole_pensee)} />
                                        <InfoLigne label="Converti(e)" val={lisible('converti', form.converti)} />
                                        {user?.gender === 'femme' && <InfoLigne label="Voile" val={lisible('porte_voile', form.porte_voile)} />}
                                        {user?.gender === 'homme' && <InfoLigne label="Barbe" val={lisible('barbe', form.barbe)} />}
                                    </div>

                                    <div style={styles.infoCard}>
                                        <h4 style={styles.infoCardTitle}>
                                            <Icon path={icons.user} size={15} color="#c0306a" /> Situation personnelle
                                        </h4>
                                        <InfoLigne label="Statut" val={lisible('marital_status', form.marital_status)} />
                                        <InfoLigne label="Profession" val={form.profession} />
                                        <InfoLigne label="Études" val={form.education} />
                                        <InfoLigne label="Origine" val={form.pays_origine} />
                                        <InfoLigne label="Ethnie" val={form.ethnie} />
                                        {user?.gender === 'homme' && form.marital_status === 'marie' && (
                                            <InfoLigne label="Épouses" val={form.nombre_epouse !== '' && form.nombre_epouse !== null && form.nombre_epouse !== undefined ? String(form.nombre_epouse) : ''} />
                                        )}
                                    </div>

                                    <div style={styles.infoCard}>
                                        <h4 style={styles.infoCardTitle}>
                                            <Icon path={icons.users} size={15} color="#c0306a" /> Famille &amp; Langues
                                        </h4>
                                        <InfoLigne label="Enfants" val={form.children !== '' && form.children !== null && form.children !== undefined ? String(form.children) : ''} />
                                        <InfoLigne label="Enfants souhaités" val={form.enfant_souhaite !== '' && form.enfant_souhaite !== null && form.enfant_souhaite !== undefined ? String(form.enfant_souhaite) : ''} />
                                        <InfoLigne label="Langues" val={form.langues} />
                                    </div>

                                    <div style={styles.infoCard}>
                                        <h4 style={styles.infoCardTitle}>
                                            <Icon path={icons.ruler} size={15} color="#c0306a" /> Physique
                                        </h4>
                                        <InfoLigne label="Taille" val={form.taille ? `${form.taille} cm` : ''} />
                                        <InfoLigne label="Poids" val={form.poids ? `${form.poids} kg` : ''} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </main>
            </div>

        </div>
    );
};

const styles = {
    page: { minHeight: '100vh', width: '100%', boxSizing: 'border-box', overflowX: 'hidden', background: 'linear-gradient(160deg, #fdfdfe 0%, #f5f6f8 50%, #eef0f3 100%)', backgroundAttachment: 'fixed' },
    sidebar: { width: '270px', flexShrink: 0, background: 'rgba(40, 5, 24, 0.55)', border: '1px solid rgba(201, 168, 76, 0.25)', borderRadius: '18px', padding: '24px 20px', height: 'fit-content', backdropFilter: 'blur(6px)' },
    sidebarHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' },
    sidebarTitle: { color: '#f3e0c7', fontSize: '18px', fontWeight: '700' },
    divider: { color: '#c9a84c', textAlign: 'center', fontSize: '13px', margin: '8px 0 20px', opacity: 0.7 },
    nav: { display: 'flex', flexDirection: 'column', gap: '6px' },
    navItem: { display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 14px', borderRadius: '10px', color: '#e8d5e0', textDecoration: 'none', fontSize: '15px', fontWeight: '500' },
    navItemBtn: { display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 14px', borderRadius: '10px', background: 'transparent', border: 'none', color: '#e8d5e0', textDecoration: 'none', fontSize: '15px', fontWeight: '500', cursor: 'pointer', width: '100%', textAlign: 'left' },
    navItemActive: { display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 14px', borderRadius: '10px', background: 'linear-gradient(135deg, #6d0f3a, #9c2059)', color: '#fff', textDecoration: 'none', fontSize: '15px', fontWeight: '700', boxShadow: '0 3px 12px rgba(0,0,0,0.3)', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' },
    tipBox: { marginTop: '24px', padding: '18px 16px', borderRadius: '14px', background: 'rgba(20, 2, 12, 0.5)', border: '1px solid rgba(201,168,76,0.2)', textAlign: 'center' },
    tipCrown: { marginBottom: '6px' },
    tipTitle: { color: '#f3e0c7', fontSize: '15px', margin: '0 0 8px', fontWeight: '700' },
    tipText: { color: '#d8c0cc', fontSize: '12.5px', lineHeight: '1.5', fontStyle: 'italic', margin: 0 },
    tipBottom: { color: '#c9a84c', fontSize: '13px', marginTop: '10px', opacity: 0.7 },
    helpBox: { marginTop: '20px', padding: '4px 4px 0' },
    helpTitle: { color: '#f3e0c7', fontSize: '15px', margin: '0 0 6px', fontWeight: '700' },
    helpText: { color: '#d8c0cc', fontSize: '12.5px', lineHeight: '1.5', margin: '0 0 12px' },
    helpBtn: { width: '100%', padding: '10px', background: 'transparent', border: '1px solid rgba(201,168,76,0.5)', borderRadius: '10px', color: '#f3e0c7', cursor: 'pointer', fontWeight: '600', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
    card: { flex: 1, background: '#ffffff', borderRadius: '24px', boxShadow: '0 12px 45px rgba(60,60,80,0.10)', border: '1px solid #eceef1', minWidth: 0 },
    vueBar: { display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' },
    vueBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '11px 20px', background: 'white', color: '#c0306a', border: '1.5px solid #f5d0e0', borderRadius: '30px', cursor: 'pointer', fontWeight: '700', fontSize: '14px' },
    vueBtnActive: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '11px 20px', background: '#c0306a', color: '#fff', border: '1.5px solid #4a0028', borderRadius: '30px', cursor: 'pointer', fontWeight: '700', fontSize: '14px', boxShadow: '0 4px 14px rgba(74,0,40,0.3)' },
    vueBtnBadge: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '18px', height: '18px', padding: '0 5px', borderRadius: '9px', backgroundColor: '#c0306a', color: '#fff', fontSize: '10.5px', fontWeight: '700' },
    title: { color: '#4a0028', fontSize: '30px', fontWeight: '800', margin: '0 0 24px', display: 'flex', alignItems: 'center', gap: '12px' },
    titleIcon: { display: 'flex', alignItems: 'center' },
    idBadge: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 16px', backgroundColor: '#fff', color: '#c0306a', border: '1.5px solid #f5d0e0', borderRadius: '30px', fontSize: '13px', fontWeight: '700', whiteSpace: 'nowrap' },
    completudeBadge: { display: 'flex', alignItems: 'center', padding: '10px 18px', backgroundColor: '#ffffff', color: '#c0306a', border: '1.5px solid #f5d0e0', borderRadius: '30px', fontSize: '14px', fontWeight: '700', boxShadow: '0 2px 8px rgba(192,48,106,0.15)' },
    photoSection: { display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '28px', padding: '22px', background: 'rgba(201,168,76,0.06)', borderRadius: '14px', border: '1px solid #ecd9b9' },
    // ═══ Grille de choix d'avatar ═══
    avatarSection: { marginBottom: '28px', padding: '22px', background: 'rgba(60,60,80,0.04)', borderRadius: '14px', border: '1px solid #eceef1' },
    avatarHead: { marginBottom: '18px' },
    avatarGrid: { display: 'flex', flexWrap: 'wrap', gap: '16px' },
    avatarChoice: { position: 'relative', width: '96px', height: '96px', borderRadius: '50%', overflow: 'hidden', border: '3px solid #eceef1', background: '#fff', cursor: 'pointer', padding: 0, flexShrink: 0, transition: 'all 0.15s' },
    avatarChoiceActive: { position: 'relative', width: '96px', height: '96px', borderRadius: '50%', overflow: 'hidden', border: '3px solid #c0306a', background: '#fff', cursor: 'pointer', padding: 0, flexShrink: 0, boxShadow: '0 4px 16px rgba(192,48,106,0.3)', transition: 'all 0.15s' },
    avatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
    avatarCheck: { position: 'absolute', bottom: '2px', right: '2px', width: '26px', height: '26px', borderRadius: '50%', background: '#c0306a', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff' },
    photoContainer: { position: 'relative', width: '110px', height: '110px', borderRadius: '50%', flexShrink: 0 },
    photo: { width: '110px', height: '110px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #c9a84c' },
    photoPlaceholder: { width: '110px', height: '110px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#4a0028', border: '3px solid #c9a84c' },
    photoBadge: { position: 'absolute', bottom: '2px', right: '2px', width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#c9a84c', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fdf2f5' },
    photoInfo: { flex: 1 },
    photoHeading: { color: '#4a0028', fontSize: '19px', margin: '0 0 6px', fontWeight: '700' },
    photoDesc: { color: '#9a7d88', fontSize: '13.5px', margin: '0 0 14px', lineHeight: '1.5' },
    photoLabel: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 22px', background: '#c0306a', color: '#f3e0c7', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '14px' },
    photoUploadBtn: { marginLeft: '10px', display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 22px', background: 'linear-gradient(135deg, #2c5f2e, #3d8b40)', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '14px' },

    tabsBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px', backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', padding: '4px 8px', WebkitOverflowScrolling: 'touch' },
    tab: { display: 'inline-flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '10px 16px', background: 'transparent', color: '#555', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', fontSize: '12px', margin: '4px 2px', whiteSpace: 'nowrap', transition: 'all 0.2s', outline: 'none', flexShrink: 0 },
    tabActive: { display: 'inline-flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '10px 16px', background: '#c0306a', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '12px', margin: '4px 2px', whiteSpace: 'nowrap', transition: 'all 0.2s', outline: 'none', boxShadow: '0 2px 8px rgba(120,24,64,0.35)', flexShrink: 0 },
    stepLine: { flex: 1, height: '2px', backgroundColor: '#eedde6', minWidth: '10px' },
    tabTop: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' },
    tabNum: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'rgba(74,0,40,0.12)', fontSize: '11px', fontWeight: '700' },
    tabPct: { fontSize: '10px', fontWeight: '700' },
    tabCheck: { display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px', fontWeight: '700', color: '#2c5f2e' },
    tabCheckCircle: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#4a0028', flexShrink: 0 },

    field: { display: 'flex', flexDirection: 'column' },
    label: { color: '#c0306a', fontWeight: '700', marginBottom: '8px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' },
    labelIcon: { display: 'flex', alignItems: 'center' },
    input: { padding: '13px 15px', borderRadius: '10px', border: '1.5px solid #c0306a', fontSize: '14.5px', fontFamily: 'inherit', outline: 'none', backgroundColor: '#fff', color: '#3a3a3a', boxSizing: 'border-box', width: '100%' },
    textarea: { width: '100%', padding: '13px 15px', borderRadius: '10px', border: '1.5px solid #c0306a', fontSize: '14.5px', fontFamily: 'inherit', outline: 'none', resize: 'vertical', backgroundColor: '#fff', color: '#3a3a3a', boxSizing: 'border-box' },
    registeredBox: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '18px', padding: '12px 16px', backgroundColor: 'rgba(201,168,76,0.1)', borderRadius: '10px', color: '#9a7d88', fontSize: '13px', fontWeight: '600' },

    // ═══ Onglet Tuteur ═══
    tuteurWrap: { maxWidth: '760px' },
    tuteurIntro: { display: 'flex', alignItems: 'flex-start', gap: '14px', padding: '18px 20px', background: 'rgba(192,48,106,0.06)', borderRadius: '14px', border: '1px solid #f5d0e0' },
    tuteurRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', backgroundColor: 'rgba(60,60,80,0.03)', borderRadius: '10px', border: '1px solid #eceef1', marginBottom: '10px' },
    tuteurStatusActive: { display: 'flex', alignItems: 'center', gap: '4px', color: '#c0306a', fontSize: '11.5px', fontWeight: '700' },
    tuteurStatusPending: { display: 'flex', alignItems: 'center', gap: '4px', color: '#b8860b', fontSize: '11.5px', fontWeight: '700' },
    tuteurRevokeBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' },

    // ═══ Vue "Présentation du profil" — layout deux colonnes ═══
    profileLayout: { display: 'grid', gap: '22px', alignItems: 'start' },

    // Colonne gauche : carte photo
    profileSideCard: { background: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(74,0,40,0.08)', border: '1px solid #ecd9b9' },
    profilePhotoBox: { width: '100%', height: '300px', backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
    profileSideBody: { padding: '20px' },
    profileSideName: { color: '#8b1a4a', fontSize: '22px', fontWeight: '700', margin: '0 0 8px', fontFamily: 'Cormorant Garamond, serif' },
    profileSideLoc: { display: 'flex', alignItems: 'center', gap: '6px', color: '#9a7d88', fontSize: '13.5px', margin: '0 0 18px' },
    apercuBox: { display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '12px 14px', background: 'rgba(201,168,76,0.1)', border: '1px solid #ecd9b9', borderRadius: '10px', color: '#7a1340', fontSize: '12.5px', lineHeight: '1.5', marginBottom: '16px' },
    editProfileBtn: { width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', background: '#c0306a', color: '#f3e0c7', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '13.5px', boxShadow: '0 3px 12px rgba(74,0,40,0.25)' },

    // Colonne droite : cartes d'informations
    profileMainCol: { display: 'flex', flexDirection: 'column', gap: '18px', minWidth: 0 },
    infoGrid: { display: 'grid', gap: '18px' },
    infoCard: { background: '#fff', borderRadius: '16px', padding: '20px 22px', boxShadow: '0 4px 20px rgba(74,0,40,0.06)', border: '1px solid #f0e4e9' },
    infoCardTitle: { display: 'flex', alignItems: 'center', gap: '8px', color: '#8b1a4a', fontSize: '15px', fontWeight: '700', margin: '0 0 14px', fontFamily: 'Cormorant Garamond, serif' },
    aboutText: { color: '#5a4a52', fontSize: '13.5px', lineHeight: '1.7', margin: 0 },
    infoLigne: { display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f0e4e9' },
    infoLabel: { color: '#9a7d88', fontSize: '13px', fontWeight: '500' },
    infoVal: { color: '#4a0028', fontSize: '13px', fontWeight: '600', textAlign: 'right' },

    navBtns: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '32px', paddingTop: '20px', borderTop: '1px solid #f0d9e3', flexWrap: 'wrap' },
    prevBtn: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '13px 24px', background: '#c0306a', color: '#f3e0c7', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: '700', fontSize: '15px', boxShadow: '0 4px 16px rgba(74,0,40,0.3)' },
    nextBtn: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '13px 30px', background: '#c0306a', color: '#f3e0c7', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: '700', fontSize: '15px', boxShadow: '0 4px 14px rgba(192,48,106,0.35)' },
    submitBtn: { display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '14px 32px', background: '#c0306a', color: '#f3e0c7', border: 'none', borderRadius: '12px', fontSize: '16px', cursor: 'pointer', fontWeight: '700', boxShadow: '0 4px 16px rgba(74,0,40,0.3)' },

    bandeauBrouillon: { backgroundColor: '#eef4fb', color: '#2c4a6b', padding: '14px 18px', margin: '0 0 4px', textAlign: 'center', borderBottom: '1px solid #cddff0' },
    bandeauAttente: { backgroundColor: '#fff8e6', color: '#8a6d1a', padding: '14px 18px', margin: '0 0 4px', textAlign: 'center', fontWeight: 600, borderBottom: '1px solid #f0e2b8' },
    bandeauRefus: { backgroundColor: '#faf3f6', color: '#6B1E35', padding: '16px 18px', margin: '0 0 4px', textAlign: 'center', borderBottom: '1px solid #e6c2d2' },
    bandeauMotif: { backgroundColor: '#fff', color: '#c0306a', padding: '8px 12px', borderRadius: '8px', margin: '6px auto', maxWidth: '520px', border: '1px solid #e6c2d2' },
    btnResoumettre: { marginTop: '12px', background: '#6B1E35', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 22px', fontWeight: 700, cursor: 'pointer', fontSize: '14px' },
    success: { backgroundColor: '#f0fff0', color: '#2c5f2e', padding: '12px', borderRadius: '10px', marginBottom: '16px', textAlign: 'center', border: '1px solid #c9a84c' },
    error: { backgroundColor: '#f2f4f7', color: '#c00', padding: '12px', borderRadius: '10px', marginBottom: '16px', textAlign: 'center' },
    cropOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', boxSizing: 'border-box' },
    cropModal: { backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '500px', boxSizing: 'border-box' },
    cropContainer: { position: 'relative', width: '100%', backgroundColor: '#333', borderRadius: '8px', overflow: 'hidden' },
    cropActions: { display: 'flex', gap: '12px', marginTop: '20px' },
    cropCancelBtn: { flex: 1, padding: '12px', backgroundColor: 'white', color: '#781840', border: '1.5px solid #e8d5e0', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' },
    cropConfirmBtn: { flex: 1, padding: '12px', background: '#c0306a', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' },
};

export default Profile;